export interface SpeechStreamHandle {
  /** Resolves when playback finishes, errors, or is stopped. */
  done: Promise<void>;
  /** Stop playback, release resources, and resolve `done`. Idempotent. */
  stop: () => void;
}

/**
 * Play an audio Response progressively -- the lever that cuts EVIE's voice lag.
 *
 * Where the browser supports streaming mp3 via MediaSource Extensions (Chrome,
 * Edge, Firefox, Android Chrome), this feeds the speak endpoint's streamed Polly
 * audio into the player AS IT ARRIVES, so EVIE starts speaking ~one chunk in
 * instead of waiting for the whole clip to be generated and downloaded. Where it
 * does not (notably iOS Safari, which lacks audio MSE), it falls back to a single
 * Blob (the full clip) -- the prior behaviour, which still works, just without the
 * head start.
 *
 * `response` is the streaming POST response from an EVIE speak endpoint. It
 * carries only generated audio, never any data, so streaming changes no boundary.
 * Returns a handle: await `done` for playback to finish; call `stop()` to cancel
 * (e.g. when voice mode is toggled off).
 */
export function playSpeechStream(response: Response): SpeechStreamHandle {
  const audio = new Audio();
  let settled = false;
  let resolveDone!: () => void;
  const done = new Promise<void>((res) => { resolveDone = res; });
  let objectUrl: string | null = null;
  let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;

  const finish = (): void => {
    if (settled) return;
    settled = true;
    audio.onended = null;
    audio.onerror = null;
    try { audio.pause(); } catch { /* ignore */ }
    if (reader) { void reader.cancel().catch(() => { /* ignore */ }); reader = null; }
    if (objectUrl) { URL.revokeObjectURL(objectUrl); objectUrl = null; }
    resolveDone();
  };

  audio.onended = finish;
  audio.onerror = finish;

  // No audio: a text-only locale answers 204 (no body). Resolve immediately so
  // the voice loop advances instead of trying to play an empty clip.
  if (response.status === 204) {
    finish();
    return { done, stop: finish };
  }

  const body = response.body;
  const canStream =
    typeof MediaSource !== 'undefined'
    && typeof MediaSource.isTypeSupported === 'function'
    && MediaSource.isTypeSupported('audio/mpeg')
    && body != null;

  if (canStream && body) {
    // ── Progressive MSE path: append Polly chunks as they stream in ──
    const mediaSource = new MediaSource();
    objectUrl = URL.createObjectURL(mediaSource);
    audio.src = objectUrl;
    mediaSource.addEventListener('sourceopen', () => {
      if (settled) return;
      let sb: SourceBuffer;
      try {
        sb = mediaSource.addSourceBuffer('audio/mpeg');
      } catch {
        finish();
        return;
      }
      reader = body.getReader();
      const queue: Uint8Array[] = [];
      let streamEnded = false;

      // Append one queued chunk per turn (SourceBuffer can't append while it's
      // still updating); when the stream is done and the queue drains, close it.
      const flush = (): void => {
        if (settled || sb.updating) return;
        const next = queue.shift();
        if (next) {
          // Cast: the stream yields Uint8Array<ArrayBufferLike>, which TS's strict
          // typed-array generics don't accept as BufferSource (it wants a plain
          // ArrayBuffer-backed view). The bytes are identical at runtime.
          try { sb.appendBuffer(next as unknown as BufferSource); } catch { finish(); }
          return;
        }
        if (streamEnded && mediaSource.readyState === 'open') {
          try { mediaSource.endOfStream(); } catch { /* ignore */ }
        }
      };
      sb.addEventListener('updateend', flush);

      const pump = (): void => {
        if (!reader) return;
        reader.read().then(({ done: rDone, value }) => {
          if (settled) return;
          if (rDone) { streamEnded = true; flush(); return; }
          if (value && value.byteLength > 0) {
            queue.push(value);
            flush();
            // First chunk in -> start playing immediately (a user gesture began
            // this, so autoplay is allowed; ignore a policy rejection).
            if (audio.paused) void audio.play().catch(() => { /* policy */ });
          }
          pump();
        }).catch(() => { streamEnded = true; flush(); });
      };
      pump();
    });
  } else {
    // ── Fallback: whole-clip Blob (no progressive head start) ──
    response.blob().then((blob) => {
      if (settled) return;
      objectUrl = URL.createObjectURL(blob);
      audio.src = objectUrl;
      void audio.play().catch(() => finish());
    }).catch(() => finish());
  }

  return { done, stop: finish };
}
